import FVector from '@data/geometry/FVector.js';
import * as THREE from 'three';
import Grid from '../Geodata/grid.js';
import { create2DPointsShape, createMultiLineShape, createRaysShape } from '../Graphics/ShapeBuilder.js';
import { getSimulationItems } from '../Gui/store/persistent/persistentStore.js';
import { getViewer } from '../store/viewerStore.js';
import { safeMapValue } from '../Utils/UtilityFunctions.js';
import Layer from './Layer.js';
import { SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA } from '@visualizer/types';
import { getSimConfig } from './SimConfig.js';

import { MultiLineJumper } from 'Loader/serialization/jumper/MultiLineJumper.js';
import { RaysJumper } from 'Loader/serialization/jumper/RaysJumper.js';
import { Jumper } from 'Loader/serialization/jumper/Jumper.js';
import { getSegments } from '../Gui/store/persistent/persistentStore.js';

export default class RaysHandler {
    raysShape: THREE.Group | null = null;
    parentLayer: Layer;

    private jumper: Jumper;
    private static raysListeners = new Set<() => void>();

    constructor(parentLayer: Layer, rayJumper: Jumper) {
        this.parentLayer = parentLayer;
        this.jumper = rayJumper;
    }

    clear() {
        this.jumper.dispose();
        this.disposeRays();
    }

    disposeRays() {
        if (!this.raysShape) return;
        getViewer().scene.remove(this.raysShape);
        if (this.raysShape) {
            this.raysShape.traverse((obj) => {
                const mesh = obj as THREE.Mesh;

                if ((mesh as any).geometry) {
                    mesh.geometry.dispose();
                }

                if ((mesh as any).material) {
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((m) => {
                            if ((m as any).map?.dispose) (m as any).map.dispose();
                            m.dispose();
                        });
                    } else {
                        if ((mesh.material as any).map?.dispose) (mesh.material as any).map.dispose();
                        mesh.material.dispose();
                    }
                }
            });
        }
        this.raysShape = null;
        RaysHandler.emitRaysChanged();
    }

    createRays(sensorDataIDs: number[], mode: 'visible' | 'all') {
        console.log('Creating rays for sensor IDs:', sensorDataIDs);
        if ([SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA].includes(this.parentLayer.getSimKind())) {
            this.createStandardRays(sensorDataIDs, mode);
            return;
        }
        this.createMultiLines(sensorDataIDs, mode);
    }

    /**
     * We can keep it there for now, it was used by heatmap
     * @param params1
     * @param params2
     * @param x
     * @param y
     * @returns
     */
    findClosestIndex(params1: number[], params2: number[], x: number, y: number) {
        if (params1.length !== params2.length || params1.length === 0) {
            throw new Error('The arrays must have the same non-zero length');
        }

        return params1.reduce(
            (closest, current, index) => {
                const distance = Math.sqrt(Math.pow(x - current, 2) + Math.pow(y - params2[index], 2));
                return distance < closest.distance ? { index, distance } : closest;
            },
            { index: -1, distance: Infinity }
        ).index;
    }

    /**
     * We can keep it there for now, it was used by heatmap
     * @param fakeRays
     * @param x0
     * @param y0
     * @returns
     */
    computeClosestRay(fakeRays: { param1: number; param2: number }[], x0: number, y0: number) {
        if (!Array.isArray(fakeRays) || fakeRays.length === 0) {
            throw new Error('The input must be a non-empty array of objects');
        }

        let closestRay: { param1: number; param2: number; index: number } | null = null;
        let minDistance = Infinity;

        for (let index = 0; index < fakeRays.length; index++) {
            const ray = fakeRays[index];
            const distance = Math.sqrt(Math.pow(x0 - ray.param1, 2) + Math.pow(y0 - ray.param2, 2));
            if (distance < minDistance) {
                minDistance = distance;
                closestRay = { ...ray, index };
            }
        }

        return closestRay;
    }

    scaleRay(ray: { startPoint: FVector; endPoint: FVector }, scaleFactor: number) {
        // Compute the direction from the start to the end:
        const vector = new FVector(ray.endPoint.x - ray.startPoint.x, ray.endPoint.y - ray.startPoint.y, ray.endPoint.z - ray.startPoint.z);

        // Scale the vector to the desired length:
        const scaledVector = new FVector(vector.x * scaleFactor, vector.y * scaleFactor, vector.z * scaleFactor);

        // Update the endpoint by adding the scaled vector to the start point
        ray.endPoint.x = ray.startPoint.x + scaledVector.x;
        ray.endPoint.y = ray.startPoint.y + scaledVector.y;
        ray.endPoint.z = ray.startPoint.z + scaledVector.z;
    }

    createStandardRays(sensorDataIDs: number[], mode: 'visible' | 'all') {
        const jumper = this.getRaysJumper();
        const segments = getSegments();
        const { linesLength, freeTargetLinesLength, showFreeLines, showIntersectingLines, raysEveryN, raysQuantity, heatmapX, heatmapY } =
            getSimulationItems();
        const frameIDs = segments.isAggregate ? segments.framesList : [segments.frameSelected];
        const simKind = this.parentLayer.getSimKind();
        const effectiveLinesLength = simKind === SIM_VVA || simKind === SIM_TARGET ? freeTargetLinesLength : linesLength;
        const scaleFactor = effectiveLinesLength / 100;

        const [vertices, colors, endingPoints]: number[][] = [[], [], []];
        const [freeVertices, freeColors, freeEndingPoints]: number[][] = [[], [], []];
        const [intVertices, intColors, intEndingPoints]: number[][] = [[], [], []];

        let minValue: number = Infinity;
        let maxValue: number = -Infinity;

        if (mode === 'visible') {
            frameIDs.forEach((frameID) => {
                sensorDataIDs.forEach((ID) => {
                    const rays = jumper.getRays(ID, frameID);
                    for (const ray of rays) {
                        const value = ray.value;
                        if (value >= 0) {
                            if (value < minValue) minValue = value;
                            if (value > maxValue) maxValue = value;
                        }
                    }
                });
            });
        } else if (mode === 'all') {
            minValue = jumper.getMin();
            maxValue = jumper.getMax();
        }

        // const storage = this.raysStorage as RayStore[][];
        frameIDs.forEach((frameID) => {
            sensorDataIDs.forEach((ID) => {
                if (ID >= Grid.sensorPointsN) return;

                // =====================================
                // 0) Read Ray Values with Jumper:
                // =====================================
                const sensorPointPosition = Grid.notNullSensorPoints[ID].getCenter();

                const rays = jumper.getRays(ID, frameID);
                const rayValues = rays.map((r) => r.value);
                const fakeRays = Array.from({ length: rays.length }, (_, i) => ({
                    endPoint: this.computeRayEndPointWithDir(rays[i].dir, sensorPointPosition, rays[i].value, simKind),
                    startPoint: sensorPointPosition,
                    value: rays[i].value,
                    index: i,
                    param1: rays[i].params?.param1,
                    param2: rays[i].params?.param2,
                }));

                const distances = fakeRays.map((ray) => ray.startPoint.distanceTo(ray.endPoint));
                const longestDistance = Math.max(...distances);
                const reducedLongestDistance = longestDistance * scaleFactor;

                const absValues = new Float32Array(rayValues.length);
                for (let i = 0; i < rayValues.length; i++) {
                    const v = Math.abs(rayValues[i]);
                    absValues[i] = v;
                }

                const tmpColor = new THREE.Color();

                const processRay = (ray: any, color: THREE.Color) => {
                    const rayDistance = ray.startPoint.distanceTo(ray.endPoint);
                    if (rayDistance > reducedLongestDistance) {
                        this.scaleRay(ray, reducedLongestDistance / rayDistance);
                    }

                    const positions: number[] = [
                        ray.startPoint.x,
                        ray.startPoint.y,
                        ray.startPoint.z,
                        ray.endPoint.x,
                        ray.endPoint.y,
                        ray.endPoint.z,
                    ];
                    vertices.push(...positions);
                    colors.push(color.r, color.g, color.b, color.r, color.g, color.b);
                    endingPoints.push(ray.endPoint.x, ray.endPoint.y, ray.endPoint.z);

                    if (ray.value > 0) {
                        freeVertices.push(...positions);
                        freeColors.push(color.r, color.g, color.b, color.r, color.g, color.b);
                        freeEndingPoints.push(ray.endPoint.x, ray.endPoint.y, ray.endPoint.z);
                    } else {
                        intVertices.push(...positions);
                        intColors.push(color.r, color.g, color.b, color.r, color.g, color.b);
                        intEndingPoints.push(ray.endPoint.x, ray.endPoint.y, ray.endPoint.z);
                    }
                };

                if (raysQuantity == 0) {
                    const closestRay = this.computeClosestRay(
                        fakeRays.map((fr) => ({
                            param1: fr.param1!,
                            param2: fr.param2!,
                        })),
                        heatmapX,
                        heatmapY
                    );
                    if (closestRay) {
                        this.computeLineColorFast(closestRay.index, simKind, rayValues, absValues, minValue, maxValue, tmpColor);
                        processRay(closestRay, tmpColor);
                    }
                } else if (raysQuantity == 1) {
                    fakeRays.forEach((ray, index) => {
                        if ((!showIntersectingLines && rays[index].value < 0) || (!showFreeLines && rays[index].value >= 0)) return;
                        if (index % raysEveryN != 0) return;
                        this.computeLineColorFast(index, simKind, rayValues, absValues, minValue, maxValue, tmpColor);
                        processRay(ray, tmpColor);
                    });
                } else if (raysQuantity == 2) {
                    fakeRays.forEach((ray, index) => {
                        if ((!showIntersectingLines && rays[index].value < 0) || (!showFreeLines && rays[index].value >= 0)) return;
                        this.computeLineColorFast(index, simKind, rayValues, absValues, minValue, maxValue, tmpColor);
                        processRay(ray, tmpColor);
                    });
                }
            });
        });

        const lineWidth = raysQuantity == 0 ? 4 : 2;
        const lineOpacity = raysQuantity == 0 ? 1 : 0.9;
        const freeRaysShape = createRaysShape(freeVertices, freeColors, lineOpacity, lineWidth);
        const intRaysShape = createRaysShape(intVertices, intColors, lineOpacity / 2, lineWidth);
        const freePointsShape = create2DPointsShape(freeEndingPoints, 4, false);
        const intPointsShape = create2DPointsShape(intEndingPoints, 2, false);

        [freeRaysShape, intRaysShape, freePointsShape, intPointsShape].forEach((shape) => (shape.renderOrder = 1));

        const group = new THREE.Group();
        group.add(freeRaysShape, intRaysShape, freePointsShape, intPointsShape);
        this.raysShape = group;
        getViewer().scene.add(this.raysShape);
        RaysHandler.emitRaysChanged();
    }

    createMultiLines(sensorDataIDs: number[], mode: 'visible' | 'all') {
        const jumper = this.getMultiLineJumper();
        const { operator } = this.parentLayer.status;
        const { linesColorMode } = getSimulationItems();
        const simKind = this.parentLayer.getSimKind();

        const multiLinesGroup = new THREE.Group();
        const tmpColor = new THREE.Color();

        let minValue: number = Infinity;
        let maxValue: number = -Infinity;

        if (mode === 'visible') {
            for (let ID of sensorDataIDs) {
                const mlts = jumper.getMultiLines(ID);
                for (const mlt of mlts) {
                    if (mlt.value) {
                        // const value = Math.abs(mlt.value);
                        const value = mlt.value;
                        if (value >= 0) {
                            if (value < minValue) minValue = value;
                            if (value > maxValue) maxValue = value;
                        }
                    } else {
                        for (const point of mlt.points) {
                            if (point.value) {
                                // const value = Math.abs(point.value);
                                const value = point.value;
                                if (value >= 0) {
                                    if (value < minValue) minValue = value;
                                    if (value > maxValue) maxValue = value;
                                }
                            }
                        }
                    }
                }
            }
        } else if (mode === 'all') {
            minValue = jumper.getMin();
            maxValue = jumper.getMax();
        }

        for (let ID of sensorDataIDs) {
            if (ID >= Grid.sensorPointsN) continue;

            const multiLinesRaw = jumper.getMultiLines(ID);
            if (!multiLinesRaw || multiLinesRaw.length === 0) continue;

            if (!isFinite(minValue) || !isFinite(maxValue)) continue;

            // =====================================================
            // PASS 2 — build geometry using shared min/max
            // =====================================================
            for (let m = 0; m < multiLinesRaw.length; m++) {
                if (operator.RAYS_QUANTITY === 0 && m >= 1) continue;
                if (operator.RAYS_QUANTITY === 1 && m % operator.RAYS_FIXED_QUANTITY !== 0) continue;

                const ml = multiLinesRaw[m];
                const points = ml.points;

                // -----------------------------
                // 1) Values per segment
                // -----------------------------
                const values =
                    ml.value !== undefined ? new Float32Array(points.length).fill(ml.value) : Float32Array.from(points.map((p) => p.value));

                const absValues = new Float32Array(values.length);
                for (let i = 0; i < values.length; i++) {
                    absValues[i] = Math.abs(values[i]);
                }

                // -----------------------------
                // 3) Build geometry
                // -----------------------------
                const vertices: number[] = [];
                const colors: number[] = [];

                for (let i = 0; i < points.length; i++) {
                    const p = points[i].point;
                    vertices.push(p.x, p.y, p.z);

                    // In Bi-Color mode, keep one solid color per full multi-line:
                    const colorIndex = linesColorMode.isBicolor ? m : i;
                    this.computeLineColorFast(colorIndex, simKind, values as unknown as number[], absValues, minValue, maxValue, tmpColor);

                    colors.push(tmpColor.r, tmpColor.g, tmpColor.b);
                }

                const lineSegments = createMultiLineShape(vertices, colors, 1.5);
                if (lineSegments) multiLinesGroup.add(lineSegments);
            }
        }

        this.raysShape = multiLinesGroup;
        getViewer().scene.add(this.raysShape);
        RaysHandler.emitRaysChanged();
    }

    static onRaysChanged(cb: () => void): () => void {
        RaysHandler.raysListeners.add(cb);
        return () => {
            RaysHandler.raysListeners.delete(cb);
        };
    }

    private static emitRaysChanged(): void {
        RaysHandler.raysListeners.forEach((cb) => {
            try {
                cb();
            } catch (e) {
                console.error('[RaysHandler] listener failed', e);
            }
        });
    }

    computeRayEndPointWithDir(dir: FVector, spPosition: FVector, value: number, simKind: number): FVector {
        switch (simKind) {
            case SIM_SUN:
            case SIM_SUN4D:
                return FVector.mult(dir, 2000).add(spPosition);
            case SIM_VVA:
                return FVector.mult(dir, value).add(spPosition);
            case SIM_TARGET:
                return dir;
            default:
                throw new Error('This simKind do not exist');
        }
    }

    private getRaysJumper(): RaysJumper {
        if (!(this.jumper instanceof RaysJumper)) {
            throw new Error(`Expected RaysJumper but found ${this.jumper.kind}`);
        }
        return this.jumper;
    }

    private getMultiLineJumper(): MultiLineJumper {
        if (!(this.jumper instanceof MultiLineJumper)) {
            throw new Error(`Expected MultiLineJumper but found ${this.jumper.kind}`);
        }
        return this.jumper;
    }

    computeLineColorFast(
        index: number,
        simKind: number,
        rayValues: number[],
        absValues: Float32Array,
        minValue: number,
        maxValue: number,
        target: THREE.Color
    ) {
        const { raysHueRange } = getSimConfig(simKind);
        const { linesColorMode } = getSimulationItems();

        const rayValue = rayValues[index];
        const absValue = absValues[index];

        let h: number, s: number, l: number;
        h = rayValue < 0 ? linesColorMode.OBSTRUCTED_LINES_H : safeMapValue(absValue, minValue, maxValue, raysHueRange[0], raysHueRange[1]);
        s = rayValue < 0 ? linesColorMode.OBSTRUCTED_LINES_S : linesColorMode.Lines_S;
        l = rayValue < 0 ? linesColorMode.OBSTRUCTED_LINES_L : linesColorMode.Lines_L;

        if (linesColorMode.isBicolor) {
            h = index & 1 ? raysHueRange[0] : raysHueRange[1];
        }

        target.setHSL(h, s, l);
    }
}
